// client/src/components/Routing/TerritoryWorkbench/WeekViz/WeekSidebar.js
import { WEEKDAYS, WEEKDAY_COLORS } from './weekColors'
import { parseYmd } from '../scheduleUtils'
import { BLOCK_COLORS, blockSegments, formatTimeFromMinutes } from './weekDayBars'

function formatDateShort (dateStr) {
  if (!dateStr) return ''
  const d = parseYmd(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function ExpectedDayBar ({ blocks, startMin, endMin }) {
  const segments = blockSegments(blocks)
  const totalMin = endMin - startMin
  const totalHoursLabel = totalMin > 0 ? `${(totalMin / 60).toFixed(1)}h` : ''

  if (segments.length === 0) {
    return (
      <div style={{
        height: 10, borderRadius: 4, marginTop: 6,
        background: 'transparent',
        borderTop: '1px dotted var(--df-line, #e0d8c2)'
      }} />
    )
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{
        display: 'flex', height: 10, borderRadius: 4, overflow: 'hidden',
        background: 'var(--df-line, #ede6d2)'
      }}>
        {segments.map((seg, i) => (
          <div key={i} style={{
            width: `${seg.widthPct}%`, background: seg.color, height: '100%'
          }} title={`${seg.kind}: ${seg.min}m`} />
        ))}
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontSize: 10.5, color: 'var(--df-ink-4, #97a094)', marginTop: 2
      }}>
        <span>{formatTimeFromMinutes(startMin)}</span>
        <span style={{ color: 'var(--df-ink-3, #6f7a6b)', fontWeight: 600 }}>{totalHoursLabel}</span>
        <span>{formatTimeFromMinutes(endMin)}</span>
      </div>
    </div>
  )
}

function LegendChip ({ kind, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, marginRight: 10 }}>
      <span style={{ width: 9, height: 9, background: BLOCK_COLORS[kind], borderRadius: 2 }} />
      {label}
    </span>
  )
}

export default function WeekSidebar ({
  focusCrewId, isFleetMode, availableCrews,
  days, missingDates,
  activeDay, setActiveDay, setHoverDay
}) {
  // Each day's date is provided by the server payload; we no longer need focusDate.
  // Aggregate sidebar header totals across all visible days
  const totalStops = days.reduce((a, d) => a + (d.totals?.stopCount || 0), 0)
  const totalSvcHours = Math.round(days.reduce((a, d) => a + (d.totals?.workMin || 0), 0) / 60)

  const scopeLabel = isFleetMode
    ? `All crews (${availableCrews?.length || 0})`
    : (availableCrews?.find(c => String(c.crewId) === String(focusCrewId))?.crewName || 'Crew')

  return (
    <div style={{
      width: 312, flexShrink: 0, borderLeft: '1px solid #e0d8c2',
      background: '#fbf8f1', display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{ padding: '15px 16px 13px', borderBottom: '1px solid #ede6d2' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: '#1c2419' }}>The week at a glance</div>
        <div style={{ fontSize: 11.5, color: '#6f7a6b', marginTop: 2 }}>
          {scopeLabel} · {totalStops} stops · {totalSvcHours}h service
        </div>
        {missingDates?.length > 0 && (
          <div style={{
            marginTop: 8, fontSize: 11, color: '#8c5b00',
            background: '#fdf4dc', borderRadius: 6, padding: '4px 8px', display: 'inline-block'
          }}>
            {missingDates.length} crew-day{missingDates.length === 1 ? '' : 's'} not yet synced
          </div>
        )}
      </div>

      {/* Expected-day legend */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #ede6d2', background: '#faf6ee' }}>
        <div style={{
          fontSize: 10, fontWeight: 800, color: '#97a094',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4
        }}>Expected day</div>
        <div>
          <LegendChip kind="work" label="Work" />
          <LegendChip kind="drive" label="Drive" />
          <LegendChip kind="break" label="Break" />
          <LegendChip kind="lunch" label="Lunch" />
        </div>
      </div>

      {/* Per-day list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 14px' }}>
        {days.map((d, i) => {
          const on = activeDay === d.dayIndex
          const fade = activeDay != null && !on
          const empty = (d.totals?.totalMin || 0) === 0
          return (
            <div
              key={`${d.date}-${i}`}
              onClick={() => setActiveDay(on ? null : d.dayIndex)}
              onMouseEnter={() => setHoverDay(d.dayIndex)}
              onMouseLeave={() => setHoverDay(null)}
              style={{
                padding: '10px 4px', borderBottom: '1px solid #ede6d2',
                cursor: 'pointer', opacity: fade ? 0.5 : 1, transition: 'opacity 0.13s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <span style={{
                  width: 11, height: 11, borderRadius: '50%',
                  background: WEEKDAY_COLORS[d.dayIndex], flexShrink: 0,
                  border: '2px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }} />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1c2419' }}>
                    {WEEKDAYS[d.dayIndex]?.full}
                  </span>
                  <span style={{ fontSize: 11, color: '#97a094' }}>{formatDateShort(d.date)}</span>
                </div>
              </div>
              <ExpectedDayBar blocks={d.blocks || []} startMin={d.startMin} endMin={d.endMin} />
              <div style={{ fontSize: 11, color: '#6f7a6b', marginTop: 4 }}>
                {empty
                  ? <i>No work scheduled</i>
                  : (
                    <>
                      <b style={{ color: '#1c2419' }}>{d.totals.stopCount}</b> stops
                      · {(d.totals.workMin / 60).toFixed(1)}h work
                      · {(d.totals.driveMin / 60).toFixed(1)}h drive
                      {d.areaName ? ` · ${d.areaName}` : ''}
                    </>
                  )
                }
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
